import { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import CellWithStatusHistory from './HistoryTable/CellWithStatus';
import CommentCell from './CommentCell';

import { Breakpoints } from 'hooks/Screen/useScreenSize';
import { TrapActivationHistory } from 'api/traps/types';

export const useGetMCTColumns = (type: string): TableColumn<TrapActivationHistory>[] => {
  const { t } = useTranslation();

  const columns: TableColumn<TrapActivationHistory>[] = [];

  if (type !== 'srbm') {
    columns.push({
      name: t('MCTDetail.TrapHistoryCard.dateTime'),
      selector: row => dayjs(row.order_datetime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss'),
      sortable: true,
      sortField: 'order_datetime',
      grow: 2,
    });
  }

  if (type === 'mct') {
    columns.push({
      name: t('MCTDetail.TrapHistoryCard.capacity'),
      selector: row => row.capacity || 0,
      hide: Breakpoints.Desktop,
    });
  }

  columns.push({
    name: t('MCTDetail.TrapHistoryCard.token'),
    selector: row => row.msg_token,
    hide: Breakpoints.Desktop,
  });

  if (type === 'srbm') {
    columns.push(
      {
        name: t('MCTDetail.TrapHistoryCard.StartActivity'),
        selector: row => dayjs(row.trigger_start_time).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss'),
        sortable: true,
        sortField: 'trigger_start_time',
        grow: 2,
      },
      {
        name: t('MCTDetail.TrapHistoryCard.EndActivity'),
        selector: row => dayjs(row.trigger_end_time).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss'),
        sortable: true,
        sortField: 'trigger_end_time',
        grow: 2,
      },
      {
        name: t('MCTDetail.TrapHistoryCard.startWeight'),
        selector: row => row.weight_before?.toString() || '-',
        sortable: true,
        grow: 1,
      },
      {
        name: t('MCTDetail.TrapHistoryCard.endWeight'),
        selector: row => row.weight_after?.toString() || '-',
        sortable: true,
        grow: 1,
      },
      {
        name: t('MCTDetail.TrapHistoryCard.BaitConsumption'),
        selector: row => row.eaten_bait?.toString() || '-',
        sortable: true,
        sortField: 'eaten_bait',
        grow: 1,
      },
    );
  }

  if (type !== 'srbm' && type !== 'srst') {
    columns.push({
      name:
        type === 'srst' || type === 'smst'
          ? t('MCTDetail.TrapHistoryCard.triggeredTrap')
          : t('MCTDetail.TrapHistoryCard.enter'),
      selector: row => row.enter,
      sortable: true,
      sortField: 'enter',
      hide: Breakpoints.Tablet,
      cell: row => (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ paddingLeft: `${row.enter === 'R' ? '14px' : '0'}` }}
        >
          {row.enter === 'R' && (
            <div>
              <i className="ri-arrow-right-line" />
            </div>
          )}
          <div>{row.enter}</div>
          {row.enter === 'L' && (
            <div>
              <i className="ri-arrow-left-line" />
            </div>
          )}
        </div>
      ),
    });
  }

  columns.push(
    {
      name: t('MCTDetail.TrapHistoryCard.rrsi'),
      selector: row => row.rssi,
      sortable: true,
      sortField: 'rssi',
      hide: Breakpoints.Desktop,
    },
    {
      name: t('MCTDetail.TrapHistoryCard.snr'),
      selector: row => row.snr,
      sortable: true,
      sortField: 'snr',
      hide: Breakpoints.Desktop,
    },
    {
      name: t('MCTDetail.TrapHistoryCard.battery'),
      selector: row => row.battery_percentage,
      sortable: true,
      sortField: 'battery_percentage',
      hide: Breakpoints.Tablet,
    },
    {
      name: t('MCTDetail.TrapHistoryCard.status'),
      selector: row => row.approv_status || '',
      cell: row => <CellWithStatusHistory {...row} />,
      hide: Breakpoints.Mobile,
      // make the column never wrap and always at least 140px wide
      style: {
        justifyContent: 'center',
      },
      minWidth: '100px',
    },
    {
      name: t('MCTDetail.TrapHistoryCard.comment'),
      selector: row => row.comment || '-',
      hide: Breakpoints.LargeDesktop,
      width: '150px',
      cell: row => <CommentCell row={row} type={type} />,
    },
  );

  return columns;
};
